import { inject, Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { serverTimestamp, Timestamp } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom } from 'rxjs';
import {
  AcademicYearRollover,
  ArchiveOutcome,
  RolloverResult,
  RolloverStatus,
  RolloverStudent,
  RolloverStudentStatus,
  SchoolArchive,
  SchoolArchiveShare,
} from '../models/academic-year-rollover.model';
import { SchoolMember, SchoolMemberType } from '../models/member.school.model';
import { MembershipStatus } from '../models/membership.status';

/** How long a leader keeps the draft to themself after their last save */
const LOCK_MINUTES = 30;
const BATCH_SIZE = 450;

/**
 * New Academic Year rollover: the wizard saves decisions to a draft and
 * `school_academicYear_finalize` applies them in one server call.
 */
@Injectable({
  providedIn: 'root',
})
export class AcademicYearService {
  private afs = inject(AngularFirestore);
  private functions = inject(Functions);
  private logger = inject(NGXLogger);

  private draftPath(groupId: string, fromYear: number) {
    return `groups/${groupId}/academic_year_rollovers/${fromYear}`;
  }

  private lock(uid: string) {
    return {
      lockedById: uid,
      lockExpires: Timestamp.fromMillis(Date.now() + LOCK_MINUTES * 60 * 1000),
    };
  }

  async getDraft(
    groupId: string,
    fromYear: number,
  ): Promise<AcademicYearRollover | undefined> {
    this.logger.debug(`AcademicYearService :: getDraft: ${groupId}, ${fromYear}`);
    const snap = await firstValueFrom(
      this.afs.doc<AcademicYearRollover>(this.draftPath(groupId, fromYear)).get(),
    );
    const draft = snap.exists ? (snap.data() as AcademicYearRollover) : undefined;
    this.logger.debug(
      `AcademicYearService :: getDraft: status ${draft?.status ?? 'none'}`,
    );
    return draft;
  }

  /** Returns the open draft for the school's current year, creating it if needed */
  async getOrCreateDraft(
    groupId: string,
    fromYear: number,
    uid: string,
  ): Promise<AcademicYearRollover> {
    const existing = await this.getDraft(groupId, fromYear);
    if (existing) return existing;

    const draft: AcademicYearRollover = {
      fromYear,
      toYear: fromYear + 1,
      status: RolloverStatus.draft,
      keepGradeIds: [],
      addGrades: [],
      teachers: {},
      createdById: uid,
      ...this.lock(uid),
    };
    this.logger.debug(
      `AcademicYearService :: getOrCreateDraft: creating ${JSON.stringify(draft)}`,
    );
    await this.afs.doc(this.draftPath(groupId, fromYear)).set({
      ...draft,
      created: serverTimestamp(),
      modified: serverTimestamp(),
    });
    return draft;
  }

  async saveDraft(
    groupId: string,
    fromYear: number,
    uid: string,
    changes: Partial<
      Pick<AcademicYearRollover, 'keepGradeIds' | 'addGrades' | 'teachers'>
    >,
  ): Promise<void> {
    this.logger.debug(
      `AcademicYearService :: saveDraft: ${groupId}, ${fromYear}, ${JSON.stringify(changes)}`,
    );
    await this.afs.doc(this.draftPath(groupId, fromYear)).update({
      ...changes,
      ...this.lock(uid),
      modified: serverTimestamp(),
    });
  }

  async getDraftStudents(
    groupId: string,
    fromYear: number,
  ): Promise<RolloverStudent[]> {
    const snap = await firstValueFrom(
      this.afs
        .collection<RolloverStudent>(`${this.draftPath(groupId, fromYear)}/students`)
        .get(),
    );
    const students = snap.docs.map(
      (d) => ({ ...(d.data() as RolloverStudent), memberId: d.id }) as RolloverStudent,
    );
    this.logger.debug(
      `AcademicYearService :: getDraftStudents: ${students.length} students`,
    );
    return students;
  }

  /** Every active student of the school; no paging limit */
  async getActiveStudents(groupId: string): Promise<SchoolMember[]> {
    const snap = await firstValueFrom(
      this.afs
        .collection<SchoolMember>('memberships', (ref) =>
          ref
            .where('groupId', '==', groupId)
            .where('status', '==', MembershipStatus.active)
            .where('memberType', 'array-contains', SchoolMemberType.student),
        )
        .get(),
    );
    const students = snap.docs.map(
      (d) => ({ ...(d.data() as SchoolMember), id: d.id }) as SchoolMember,
    );
    this.logger.debug(
      `AcademicYearService :: getActiveStudents: ${groupId}, ${students.length} students`,
    );
    return students;
  }

  /** Every active teacher of the school */
  async getActiveTeachers(groupId: string): Promise<SchoolMember[]> {
    const snap = await firstValueFrom(
      this.afs
        .collection<SchoolMember>('memberships', (ref) =>
          ref
            .where('groupId', '==', groupId)
            .where('status', '==', MembershipStatus.active)
            .where('memberType', 'array-contains', SchoolMemberType.teacher),
        )
        .get(),
    );
    const teachers = snap.docs.map(
      (d) => ({ ...(d.data() as SchoolMember), id: d.id }) as SchoolMember,
    );
    this.logger.debug(
      `AcademicYearService :: getActiveTeachers: ${groupId}, ${teachers.length} teachers`,
    );
    return teachers;
  }

  /**
   * Adds every active student missing from the draft (each keeps their current
   * grade by default) and drops students who are no longer active.
   */
  async syncDraftStudents(
    groupId: string,
    fromYear: number,
  ): Promise<RolloverStudent[]> {
    const [active, drafted] = await Promise.all([
      this.getActiveStudents(groupId),
      this.getDraftStudents(groupId, fromYear),
    ]);
    const activeIds = new Set(active.map((s) => s.memberId));
    // Older records store the text "unassigned" instead of leaving the grade empty
    const gradeOf = (s: SchoolMember) =>
      s.gradeId && s.gradeId !== 'unassigned' ? s.gradeId : null;
    const draftedIds = new Set(drafted.map((s) => s.memberId));

    const added: RolloverStudent[] = active
      .filter((s) => !draftedIds.has(s.memberId))
      .map((s) => ({
        memberId: s.memberId,
        name: s.name ?? '',
        photoURL: s.photoURL ?? null,
        fromGradeId: gradeOf(s),
        fromGradeName: gradeOf(s) ? s.gradeName ?? null : null,
        status: RolloverStudentStatus.active,
        newGradeId: gradeOf(s),
        skip: false,
      }));
    const gone = drafted.filter((s) => !activeIds.has(s.memberId));

    this.logger.debug(
      `AcademicYearService :: syncDraftStudents: +${added.length}, -${gone.length}`,
    );
    await this.writeStudents(groupId, fromYear, added, gone);
    return [
      ...drafted.filter((s) => activeIds.has(s.memberId)),
      ...added,
    ];
  }

  async saveStudents(
    groupId: string,
    fromYear: number,
    students: RolloverStudent[],
  ): Promise<void> {
    this.logger.debug(
      `AcademicYearService :: saveStudents: ${students.length} students, ${JSON.stringify(
        students.map((s) => ({
          memberId: s.memberId,
          status: s.status,
          newGradeId: s.newGradeId,
          skip: s.skip,
        })),
      )}`,
    );
    await this.writeStudents(groupId, fromYear, students, []);
  }

  private async writeStudents(
    groupId: string,
    fromYear: number,
    upserts: RolloverStudent[],
    deletes: RolloverStudent[],
  ): Promise<void> {
    const base = `${this.draftPath(groupId, fromYear)}/students`;
    const ops = [
      ...upserts.map((s) => ({ s, del: false })),
      ...deletes.map((s) => ({ s, del: true })),
    ];
    for (let i = 0; i < ops.length; i += BATCH_SIZE) {
      const batch = this.afs.firestore.batch();
      ops.slice(i, i + BATCH_SIZE).forEach(({ s, del }) => {
        const ref = this.afs.firestore.doc(`${base}/${s.memberId}`);
        if (del) batch.delete(ref);
        else batch.set(ref, { ...s, modified: serverTimestamp() });
      });
      await batch.commit();
    }
  }

  /** Applies the draft: archives, moves grades/teachers/students, flips the year */
  async finalize(groupId: string, fromYear: number): Promise<RolloverResult> {
    this.logger.debug(`AcademicYearService :: finalize: ${groupId}, ${fromYear}`);
    const callable = httpsCallable<
      { groupId: string; fromYear: number },
      RolloverResult
    >(this.functions, 'school_academicYear_finalize', {
      limitedUseAppCheckTokens: true,
    });
    const result = await callable({ groupId, fromYear });
    this.logger.debug(
      `AcademicYearService :: finalize: result ${JSON.stringify(result.data)}`,
    );
    return result.data;
  }

  /** A past year's roster of one grade, from school_archives */
  async getArchivedStudents(
    groupId: string,
    gradeId: string,
    academicYear: number,
  ): Promise<SchoolArchive[]> {
    this.logger.debug(
      `AcademicYearService :: getArchivedStudents: ${groupId}, ${gradeId}, ${academicYear}`,
    );
    const snap = await firstValueFrom(
      this.afs
        .collection<any>('school_archives', (ref) =>
          ref
            .where('groupId', '==', groupId)
            .where('gradeId', '==', gradeId)
            .where('gradeYear', '==', academicYear)
            .orderBy('name'),
        )
        .get(),
    );
    const archives = snap.docs.map((d) => {
      const a = d.data();
      return {
        id: d.id,
        groupId: a.groupId,
        groupName: a.groupName ?? null,
        memberId: a.memberId,
        name: a.name ?? null,
        photoURL: a.photoURL ?? null,
        academicYear: a.academicYear ?? a.gradeYear,
        gradeId: a.gradeId ?? null,
        gradeName: a.gradeName ?? null,
        teacherId: a.teacherId ?? null,
        teacherName: a.teacherName ?? null,
        outcome:
          a.outcome ??
          (a.status === MembershipStatus.alumni
            ? ArchiveOutcome.alumni
            : ArchiveOutcome.promoted),
        nextGradeId: a.nextGradeId ?? null,
      } as SchoolArchive;
    });
    this.logger.debug(
      `AcademicYearService :: getArchivedStudents: ${archives.length} records`,
    );
    return archives;
  }

  async listChildHistory(
    childUid: string,
    viaGroupId?: string,
  ): Promise<SchoolArchive[]> {
    this.logger.debug(
      `AcademicYearService :: listChildHistory: ${childUid}, via ${viaGroupId ?? '-'}`,
    );
    const callable = httpsCallable<
      { childUid: string; groupId?: string },
      SchoolArchive[]
    >(this.functions, 'school_archives_listForChild');
    const result = await callable(
      viaGroupId ? { childUid, groupId: viaGroupId } : { childUid },
    );
    this.logger.debug(
      `AcademicYearService :: listChildHistory: ${result.data.length} records`,
    );
    return result.data;
  }

  async grantShare(
    childUid: string,
    groupId: string,
  ): Promise<{ groupId: string; expiresAt: number }> {
    this.logger.debug(`AcademicYearService :: grantShare: ${childUid} -> ${groupId}`);
    const callable = httpsCallable<
      { childUid: string; groupId: string },
      { groupId: string; expiresAt: number }
    >(this.functions, 'school_archives_grantShare');
    const result = await callable({ childUid, groupId });
    this.logger.debug(
      `AcademicYearService :: grantShare: ${JSON.stringify(result.data)}`,
    );
    return result.data;
  }

  async revokeShare(childUid: string, groupId: string): Promise<void> {
    this.logger.debug(`AcademicYearService :: revokeShare: ${childUid} -> ${groupId}`);
    const callable = httpsCallable(this.functions, 'school_archives_revokeShare');
    const result = await callable({ childUid, groupId });
    this.logger.debug(
      `AcademicYearService :: revokeShare: ${JSON.stringify(result.data)}`,
    );
  }

  async listShares(childUid: string): Promise<SchoolArchiveShare[]> {
    this.logger.debug(`AcademicYearService :: listShares: ${childUid}`);
    const callable = httpsCallable<{ childUid: string }, SchoolArchiveShare[]>(
      this.functions,
      'school_archives_listShares',
    );
    const result = await callable({ childUid });
    this.logger.debug(
      `AcademicYearService :: listShares: ${result.data.length} shares`,
    );
    return result.data;
  }

  async updateOutcome(archiveId: string, outcome: ArchiveOutcome): Promise<void> {
    this.logger.debug(`AcademicYearService :: updateOutcome: ${archiveId}, ${outcome}`);
    const callable = httpsCallable(this.functions, 'school_archives_updateOutcome');
    const result = await callable({ archiveId, outcome });
    this.logger.debug(
      `AcademicYearService :: updateOutcome: ${JSON.stringify(result.data)}`,
    );
  }
}
